/* eslint-disable react/forbid-prop-types */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { MdInsertChart } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { subDays, formatISO, differenceInDays } from 'date-fns';

import { Divider } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';

import CardSideBordered from '~/components/CardSideBordered';
import DataTable from '~/components/DataTable';
import LocaleMessage from '~/components/LocaleMessage';
import PageContent from '~/components/PageContent';
import CounterCard from '~/components/Reports/CounterCard';
import ReportHeader from '~/components/Reports/ReportHeader';
import ReportPieChart from '~/components/Reports/ReportPieChart';
import Splash from '~/components/Splash/Inside';

import {
    TotalContainer,
    ChartsContainer,
    TableArea,
    RowArea,
} from '~/pages/Platform/Reports/style';
import api from '~/services/pluginbot-api';
import { expireSession } from '~/store/modules/auth/actions';
import getDateLocale from '~/util/GetDateLocale';
import lng_all from '~/util/LangMessages';
import GetPluginspaceTheme from '~/util/PluginspaceTheme';

export default function QnAUsage() {
    const dispatch = useDispatch();
    const colors = GetPluginspaceTheme(useTheme());
    const settings = useSelector(state => state.settings || null);
    const date_loc = getDateLocale(settings);
    const loc = settings.locale;
    const lang = loc && loc.code ? loc.code : 'pt_BR';
    const lng_labels = lng_all && lng_all[lang] ? lng_all[lang] : {};

    const now = new Date();
    const def_from = new Date(subDays(now, 30));

    const [untilDate, setUntilDate] = useState(now);
    const [fromDate, setFromDate] = useState(def_from);

    const [statistics, setStatistics] = useState({});
    const [groupedData, setGroupedData] = useState({});
    const [isLoading, setIsLoading] = useState(true);

    const shortcut_list = {
        today: <LocaleMessage msg="button.today" />,
        '30_days': <LocaleMessage msg="button.last_30_days" />,
        '60_days': <LocaleMessage msg="button.last_60_days" />,
        '90_days': <LocaleMessage msg="button.last_90_days" />,
        custom: <LocaleMessage msg="button.custom" />,
    };

    function getDefaultShortcut() {
        if (differenceInDays(untilDate, new Date()) !== 0) return 'custom';
        const diff = differenceInDays(untilDate, fromDate);
        if (diff === 0) return 'today';
        if (diff === 30 || diff === 60 || diff === 90) return `${diff}_days`;
        return 'custom';
    }

    const [currShortcut, setCurrShortcut] = useState(getDefaultShortcut());

    const pluginspace_header = [
        {
            id: 'pluginspace_name',
            label: 'Pluginspace',
        },
    ];

    const headers_grouped = [
        {
            id: 'name',
            label: <LocaleMessage msg="page.qna.statistics.usage.source" />,
        },
        {
            id: 'answers',
            label: <LocaleMessage msg="page.qna.statistics.usage.answers" />,
            align: 'right',
        },
        {
            id: 'input',
            label: (
                <LocaleMessage msg="page.qna.statistics.usage.input_tokens" />
            ),
            align: 'right',
        },
        {
            id: 'output',
            label: (
                <LocaleMessage msg="page.qna.statistics.usage.output_tokens" />
            ),
            align: 'right',
        },
        {
            id: 'searches',
            label: (
                <LocaleMessage msg="page.qna.statistics.usage.online_searches" />
            ),
            align: 'right',
        },
    ];

    const headers_ans_sources = [
        {
            id: 'name',
            label: <LocaleMessage msg="page.qna.statistics.usage.source" />,
        },
        {
            id: 'value',
            label: <LocaleMessage msg="table.headers.quantity" />,
            align: 'right',
        },
    ];

    const headers_ai_models = [
        {
            id: 'name',
            label: <LocaleMessage msg="page.qna.statistics.usage.ai_model" />,
        },
        {
            id: 'value',
            label: <LocaleMessage msg="table.headers.quantity" />,
            align: 'right',
        },
    ];

    function formatUsd(value) {
        const amount = Number(value) || 0;
        return `$ ${amount.toFixed(6)}`;
    }

    function requestError(error) {
        if (error.response) {
            const message = (
                <LocaleMessage msg={`errors.${error.response.data.code}`} />
            );
            toast.error(message);
            const { status } = error.response;
            if (status === 401) {
                dispatch(expireSession());
            }
        } else if (error.request) {
            toast.error(<LocaleMessage msg="errors.request" />);
        } else {
            toast.error(<LocaleMessage msg="errors.unknown" />);
        }
    }

    function parseData(data = {}) {
        const pluginspace_keys = {};
        const dialog_keys = {};

        const answers = Array.isArray(data.answers) ? data.answers : [];

        const answer_sources = [
            'dialog_content',
            'vertex_search',
            'google_search',
            'fallback',
        ];
        const a_pluginspaces = {};
        const a_dialogs = {};
        const a_sources = {};
        const a_ai_models = {};

        answer_sources.forEach(s => {
            const label_key = `list.dialogs.answer_sources.${s}`;
            a_sources[s] = {
                id: s,
                source: s,
                name: lng_labels[label_key],
                value: 0,
            };
        });

        const usage = answers.map(a => {
            const pluginspace = a.pluginspace || {};
            const dialog = a.dialog || {};
            const source = a.answer_engine || '';
            const searches = a.searches || {};
            const ai_model = a.ai_model || 'unknown';

            const pluginspace_id = pluginspace.id || '---';
            const dialog_id = dialog.id || '---';

            if (!pluginspace_keys[pluginspace_id]) {
                pluginspace_keys[pluginspace_id] = pluginspace;
            }

            if (!dialog_keys[dialog_id]) {
                dialog_keys[dialog_id] = dialog;
            }

            const tokens = a.tokens || {};

            if (!a_pluginspaces[pluginspace_id]) {
                a_pluginspaces[pluginspace_id] = {
                    answers: 0,
                    input: 0,
                    output: 0,
                    searches: 0,
                };
            }

            a_pluginspaces[pluginspace_id] = {
                ...a_pluginspaces[pluginspace_id],
                answers: a_pluginspaces[pluginspace_id].answers + 1,
                input: a_pluginspaces[pluginspace_id].input + tokens.input,
                output: a_pluginspaces[pluginspace_id].output + tokens.output,
                searches:
                    a_pluginspaces[pluginspace_id].searches +
                    (searches.google_search ? 1 : 0),
            };

            if (!a_dialogs[dialog_id]) {
                a_dialogs[dialog_id] = {
                    pluginspace_name: pluginspace.name,
                    answers: 0,
                    input: 0,
                    output: 0,
                    searches: 0,
                };
            }

            a_dialogs[dialog_id] = {
                ...a_dialogs[dialog_id],
                answers: a_dialogs[dialog_id].answers + 1,
                input: a_dialogs[dialog_id].input + tokens.input,
                output: a_dialogs[dialog_id].output + tokens.output,
                searches:
                    a_dialogs[dialog_id].searches +
                    (searches.google_search ? 1 : 0),
            };

            if (!a_sources[source]) {
                a_sources[source] = {
                    id: source,
                    source,
                    name: '---',
                    value: 0,
                };
            }

            a_sources[source] = {
                ...a_sources[source],
                value: a_sources[source].value + 1,
            };

            const model_name =
                ai_model === 'unknown'
                    ? lng_labels[
                          'page.qna.statistics.usage.ai_model.unknown'
                      ] || '---'
                    : ai_model;

            if (!a_ai_models[ai_model]) {
                a_ai_models[ai_model] = {
                    id: ai_model,
                    name: model_name,
                    value: 0,
                };
            }

            a_ai_models[ai_model] = {
                ...a_ai_models[ai_model],
                value: a_ai_models[ai_model].value + 1,
            };

            return {
                ...a,
                pluginspace_name: pluginspace.name,
                dialog_name: dialog.name,
            };
        });

        const pluginspace_grouped = Object.keys(a_pluginspaces).map(p => {
            const values = a_pluginspaces[p];
            const obj = pluginspace_keys[p];

            return {
                id: obj ? obj.id : '-',
                name: obj ? obj.name : '---',
                ...values,
            };
        });

        const dialog_grouped = Object.keys(a_dialogs).map(d => {
            const values = a_dialogs[d];
            const obj = dialog_keys[d];

            return {
                id: obj ? obj.id : '-',
                name: obj ? obj.name : '---',
                pluginspace_name: '',
                ...values,
            };
        });

        const source_grouped = answer_sources.map(s => {
            return a_sources[s] || { id: s, name: s, source: s, value: 0 };
        });

        const ai_models_grouped = Object.keys(a_ai_models).map(
            m => a_ai_models[m]
        );

        const spend = data.spend || {};
        const spend_models_grouped = (spend.by_model || []).map(item => {
            const ai_model = item.ai_model || 'unknown';
            const name =
                ai_model === 'unknown'
                    ? lng_labels[
                          'page.qna.statistics.usage.ai_model.unknown'
                      ] || '---'
                    : ai_model;
            const value = Number(item.cost) || 0;

            return {
                id: ai_model,
                name,
                value: Number(value.toFixed(8)),
                cost: formatUsd(value),
            };
        });

        const spend_pluginspaces_grouped = (spend.by_pluginspace || []).map(
            item => {
                const pluginspace = item.pluginspace || {};
                const value = Number(item.cost) || 0;

                return {
                    id: pluginspace.id || '-',
                    name: pluginspace.name || '---',
                    value: Number(value.toFixed(8)),
                    cost: formatUsd(value),
                };
            }
        );

        setGroupedData({
            pluginspaces: pluginspace_grouped,
            dialogs: dialog_grouped,
            sources: source_grouped,
            ai_models: ai_models_grouped,
            spend_models: spend_models_grouped,
            spend_pluginspaces: spend_pluginspaces_grouped,
            total_spend: Number(spend.total) || 0,
        });

        setStatistics({
            ...data,
            usage,
        });
    }

    async function loadData() {
        setIsLoading(true);
        const fromISO = formatISO(new Date(fromDate));
        const untilISO = formatISO(new Date(untilDate));
        await api
            .get(
                `qna/admin/statistics?from=${encodeURIComponent(
                    fromISO
                )}&until=${encodeURIComponent(untilISO)}`
            )
            .then(response => {
                const { data } = response;
                parseData(data);
            })
            .catch(error => requestError(error));
        setIsLoading(false);
    }

    useEffect(() => {
        setCurrShortcut(getDefaultShortcut());
    }, [fromDate, untilDate]);

    useEffect(() => {
        loadData();
    }, [fromDate, untilDate, date_loc]);

    function handleFromDateChange(date) {
        setFromDate(date);
    }

    function handleUntilDateChange(date) {
        setUntilDate(date);
    }

    function handlePeriodShortcut(shortcut) {
        const newUntil = new Date();
        switch (shortcut) {
            case 'today': {
                setFromDate(newUntil);
                setUntilDate(newUntil);
                break;
            }
            case '30_days': {
                setFromDate(new Date(subDays(newUntil, 30)));
                setUntilDate(newUntil);
                break;
            }
            case '60_days': {
                setFromDate(new Date(subDays(newUntil, 60)));
                setUntilDate(newUntil);
                break;
            }
            case '90_days': {
                setFromDate(new Date(subDays(newUntil, 90)));
                setUntilDate(newUntil);
                break;
            }
            default:
                break;
        }
        setCurrShortcut(shortcut);
    }

    function renderPieChart(data, key, title, color) {
        return (
            <ReportPieChart
                title={title}
                classes="col-md-3 col-12"
                color={color}
                data={data}
                dataKey={key}
                customization={{
                    height: 200,
                    innerRadius: 50,
                    outerRadius: 80,
                }}
            />
        );
    }

    function renderGroupedChartRow(data) {
        return (
            <>
                {renderPieChart(
                    data,
                    'answers',
                    <LocaleMessage msg="page.qna.statistics.usage.answers" />,
                    colors.dashboard_graphs
                )}
                {renderPieChart(
                    data,
                    'input',
                    <LocaleMessage msg="page.qna.statistics.usage.input_tokens" />,
                    colors.dashboard_cards
                )}
                {renderPieChart(
                    data,
                    'output',
                    <LocaleMessage msg="page.qna.statistics.usage.output_tokens" />,
                    colors.dashboard_graphs
                )}
                {renderPieChart(
                    data,
                    'searches',
                    <LocaleMessage msg="page.qna.statistics.usage.online_searches" />,
                    colors.dashboard_cards
                )}
            </>
        );
    }

    return (
        <PageContent
            title={<LocaleMessage msg="page.qna.statistics.title" />}
            breadcrumbs={[
                {
                    url: '/',
                    title: <LocaleMessage msg="breadcrumbs.home" />,
                },
                {
                    url: '/pluginbot_qna',
                    title: <LocaleMessage msg="page.qna.title" />,
                },
            ]}
            loading={false}
        >
            <div className="col-12 mb-5 mt-5">
                <CardSideBordered
                    hide
                    title={<LocaleMessage msg="page.qna.statistics.title" />}
                    Icon={MdInsertChart}
                >
                    <>
                        <div className="col-12 mt-3">
                            <ReportHeader
                                date_props={{
                                    locale: date_loc,
                                }}
                                period={{
                                    fromDate,
                                    untilDate,
                                }}
                                handleFromDateChange={handleFromDateChange}
                                handleUntilDateChange={handleUntilDateChange}
                                reloadData={() => loadData()}
                                shortcuts={{
                                    active: currShortcut,
                                    list: shortcut_list,
                                }}
                                handleShortcutClick={id =>
                                    handlePeriodShortcut(id)
                                }
                            />
                            <div className="sidecard-body mt-3">
                                {isLoading ? (
                                    <Splash />
                                ) : (
                                    <>
                                        <TotalContainer className="mt-3 mb-5">
                                            <CounterCard
                                                classes="col-md-4 col-12"
                                                title={
                                                    <LocaleMessage msg="page.qna.statistics.dialogs.all" />
                                                }
                                                value={
                                                    statistics &&
                                                    statistics.dialogs &&
                                                    statistics.dialogs.list
                                                        ? statistics.dialogs
                                                              .list.length
                                                        : 0
                                                }
                                            />
                                            <CounterCard
                                                classes="col-md-4 col-12"
                                                title={
                                                    <LocaleMessage msg="page.qna.statistics.dialogs.month" />
                                                }
                                                value={
                                                    statistics &&
                                                    statistics.dialogs &&
                                                    statistics.dialogs.recent
                                                        ? statistics.dialogs
                                                              .recent.length
                                                        : 0
                                                }
                                            />
                                            <CounterCard
                                                classes="col-md-4 col-12"
                                                title={
                                                    <LocaleMessage msg="page.qna.statistics.usage.total_spend" />
                                                }
                                                value={formatUsd(
                                                    groupedData.total_spend || 0
                                                )}
                                            />
                                        </TotalContainer>

                                        <Divider />

                                        <RowArea className="mt-5 mb-5">
                                            <p className="card-title">
                                                <LocaleMessage msg="page.qna.statistics.pluginspaces" />
                                            </p>
                                        </RowArea>

                                        <ChartsContainer className="row mb-5">
                                            {renderGroupedChartRow(
                                                groupedData.pluginspaces
                                            )}
                                        </ChartsContainer>
                                        <TotalContainer className="row mb-5">
                                            <TableArea
                                                className="col-md-10 col-12 table-responsive-material"
                                                style={{
                                                    minHeight: '350px',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <DataTable
                                                    maxHeight="100%"
                                                    headerColumns={
                                                        headers_grouped
                                                    }
                                                    data={
                                                        groupedData.pluginspaces ||
                                                        []
                                                    }
                                                    orderDirection="desc"
                                                    orderColumn="answers"
                                                    hasActions={false}
                                                    sortable
                                                    defaultRowsPerPage={10}
                                                />
                                            </TableArea>
                                        </TotalContainer>

                                        <Divider />

                                        <RowArea className="mt-5 mb-5">
                                            <p className="card-title">
                                                <LocaleMessage msg="page.qna.statistics.dialogs" />
                                            </p>
                                        </RowArea>
                                        <ChartsContainer className="row mb-5">
                                            {renderGroupedChartRow(
                                                groupedData.dialogs
                                            )}
                                        </ChartsContainer>
                                        <TotalContainer className="row mb-5">
                                            <TableArea
                                                className="col-md-10 col-12 table-responsive-material"
                                                style={{
                                                    minHeight: '350px',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <DataTable
                                                    maxHeight="100%"
                                                    headerColumns={[
                                                        ...pluginspace_header,
                                                        ...headers_grouped,
                                                    ]}
                                                    data={
                                                        groupedData.dialogs ||
                                                        []
                                                    }
                                                    orderDirection="desc"
                                                    orderColumn="answers"
                                                    hasActions={false}
                                                    sortable
                                                    defaultRowsPerPage={10}
                                                />
                                            </TableArea>
                                        </TotalContainer>

                                        <Divider />

                                        <RowArea className="mt-5 mb-5">
                                            <p className="card-title">
                                                <LocaleMessage msg="page.qna.statistics.usage.answer_sources" />
                                            </p>
                                        </RowArea>

                                        <ChartsContainer className="row mb-5">
                                            <ReportPieChart
                                                classes="col-md-6 col-12"
                                                color={colors.dashboard_graphs}
                                                data={groupedData.sources}
                                                dataKey="value"
                                                customization={{
                                                    height: 300,
                                                    innerRadius: 60,
                                                    outerRadius: 100,
                                                }}
                                            />
                                            <TableArea
                                                className="col-md-5 col-12 table-responsive-material"
                                                style={{
                                                    minHeight: '350px',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <DataTable
                                                    maxHeight="100%"
                                                    headerColumns={
                                                        headers_ans_sources
                                                    }
                                                    data={
                                                        groupedData.sources ||
                                                        []
                                                    }
                                                    orderDirection="desc"
                                                    orderColumn="value"
                                                    hasActions={false}
                                                    sortable
                                                    defaultRowsPerPage={10}
                                                    hasFooter={false}
                                                />
                                            </TableArea>
                                        </ChartsContainer>

                                        <Divider />

                                        <RowArea className="mt-5 mb-5">
                                            <p className="card-title">
                                                <LocaleMessage msg="page.qna.statistics.usage.ai_models" />
                                            </p>
                                        </RowArea>

                                        <ChartsContainer className="row mb-5">
                                            <ReportPieChart
                                                classes="col-md-6 col-12"
                                                color={colors.dashboard_cards}
                                                data={groupedData.ai_models}
                                                dataKey="value"
                                                customization={{
                                                    height: 300,
                                                    innerRadius: 60,
                                                    outerRadius: 100,
                                                }}
                                            />
                                            <TableArea
                                                className="col-md-5 col-12 table-responsive-material"
                                                style={{
                                                    minHeight: '350px',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <DataTable
                                                    maxHeight="100%"
                                                    headerColumns={
                                                        headers_ai_models
                                                    }
                                                    data={
                                                        groupedData.ai_models ||
                                                        []
                                                    }
                                                    orderDirection="desc"
                                                    orderColumn="value"
                                                    hasActions={false}
                                                    sortable
                                                    defaultRowsPerPage={10}
                                                    hasFooter={false}
                                                />
                                            </TableArea>
                                        </ChartsContainer>

                                        <Divider />

                                        <RowArea className="mt-5 mb-5">
                                            <p className="card-title">
                                                <LocaleMessage msg="page.qna.statistics.usage.spend_by_model" />
                                            </p>
                                        </RowArea>

                                        <ChartsContainer className="row mb-5">
                                            <ReportPieChart
                                                title={
                                                    <LocaleMessage msg="page.qna.statistics.usage.spend_usd" />
                                                }
                                                classes="col-md-6 col-12"
                                                color={colors.dashboard_graphs}
                                                data={
                                                    groupedData.spend_models ||
                                                    []
                                                }
                                                dataKey="value"
                                                customization={{
                                                    height: 300,
                                                    innerRadius: 60,
                                                    outerRadius: 100,
                                                }}
                                            />
                                            <TableArea
                                                className="col-md-5 col-12 table-responsive-material"
                                                style={{
                                                    minHeight: '350px',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <DataTable
                                                    maxHeight="100%"
                                                    headerColumns={[
                                                        {
                                                            id: 'name',
                                                            label: (
                                                                <LocaleMessage msg="page.qna.statistics.usage.ai_model" />
                                                            ),
                                                        },
                                                        {
                                                            id: 'cost',
                                                            label: (
                                                                <LocaleMessage msg="page.qna.statistics.usage.spend_usd" />
                                                            ),
                                                            align: 'right',
                                                        },
                                                    ]}
                                                    data={
                                                        groupedData.spend_models ||
                                                        []
                                                    }
                                                    orderDirection="desc"
                                                    orderColumn="value"
                                                    hasActions={false}
                                                    sortable
                                                    defaultRowsPerPage={10}
                                                    hasFooter={false}
                                                />
                                            </TableArea>
                                        </ChartsContainer>

                                        <Divider />

                                        <RowArea className="mt-5 mb-5">
                                            <p className="card-title">
                                                <LocaleMessage msg="page.qna.statistics.usage.spend_by_pluginspace" />
                                            </p>
                                        </RowArea>

                                        <ChartsContainer className="row mb-5">
                                            <ReportPieChart
                                                title={
                                                    <LocaleMessage msg="page.qna.statistics.usage.spend_usd" />
                                                }
                                                classes="col-md-6 col-12"
                                                color={colors.dashboard_cards}
                                                data={
                                                    groupedData.spend_pluginspaces ||
                                                    []
                                                }
                                                dataKey="value"
                                                customization={{
                                                    height: 300,
                                                    innerRadius: 60,
                                                    outerRadius: 100,
                                                }}
                                            />
                                            <TableArea
                                                className="col-md-5 col-12 table-responsive-material"
                                                style={{
                                                    minHeight: '350px',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <DataTable
                                                    maxHeight="100%"
                                                    headerColumns={[
                                                        {
                                                            id: 'name',
                                                            label:
                                                                'Pluginspace',
                                                        },
                                                        {
                                                            id: 'cost',
                                                            label: (
                                                                <LocaleMessage msg="page.qna.statistics.usage.spend_usd" />
                                                            ),
                                                            align: 'right',
                                                        },
                                                    ]}
                                                    data={
                                                        groupedData.spend_pluginspaces ||
                                                        []
                                                    }
                                                    orderDirection="desc"
                                                    orderColumn="value"
                                                    hasActions={false}
                                                    sortable
                                                    defaultRowsPerPage={10}
                                                    hasFooter={false}
                                                />
                                            </TableArea>
                                        </ChartsContainer>
                                    </>
                                )}
                            </div>
                        </div>
                    </>
                </CardSideBordered>
            </div>
        </PageContent>
    );
}

QnAUsage.propTypes = {};
